import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { connect, useSelector } from 'react-redux'
import { ProgramSpace } from '../types'
import AreaItemsRow from './AreaItemsRow'
import config from '../config'
import TimeGrid from './TimeGrid'
import { mapGetDayWidthToProps } from '../redux/selectors'
import ZoomControl from './ZoomControl'
import colors from '../../../constants/colors'
import DateControl from './DateControl'
import { getSpacingLeft } from '../calculateSpace'
import { RootState } from '../../../store'

interface StyledTimeTableWidthProps {
    dayWidth: number
}

const S = {
    Layout: styled.div`
        display: grid;
        grid-template-columns: ${config.headerColumnWidth}px auto;
        grid-template-rows: ${config.timelineNavHeight}px auto;
        grid-template-areas: 
        "top-left right"
        "bottom-left right";
    `,
    DateSelectWrapper: styled.div`
        grid-area: top-left;
        background-color: ${config.timelineNavColor};
    `,
    HeaderColumnWrapper: styled.div`
        grid-area: bottom-left;
        background-color: grey;
    `,
    TimeTableWrapper: styled.div`
        grid-area: right;
        position: relative;
        overflow-x: scroll;
        // This works on safari and chrome only:
        overflow-x: overlay;
        overflow-y: hidden;
        scroll-behavior: smooth;
        max-width: calc(100vw - ${config.headerColumnWidth}px);
        height: 100%;
    `,
    TimeTableWidth: connect(mapGetDayWidthToProps)(styled.div<StyledTimeTableWidthProps>`
        width: ${({ dayWidth }) => dayWidth}px;
        height: 100%;
    `),
    AreaHeader: styled.div`
        width: 100%;
        font-size: 18px;
        height: ${config.rowHeight}px;
        padding-left: 12px;
        display: flex;
        align-items: center;
        background-color: ${colors.gray6};
        color: white;
    
        // easy way to create border while centering content
        margin-bottom: 1px; 
    `,
    ZoomPosition: styled.div`
        position: absolute;
        right: 0;
    `,
}

interface TimeTableProps {
    programSpaces: ProgramSpace[]
    expanded: boolean
}

const TimeTable = ({ programSpaces = [], expanded }: TimeTableProps) => {
    const tableRef = useRef<HTMLDivElement>()

    const scrollToEnter = () => {
        const tableWidth = tableRef.current.scrollWidth || 0
        const spacingLeftPercentage = getSpacingLeft(new Date()) // percentage string (eg: "75.06%")
        const spacingLeft = (
            +spacingLeftPercentage.slice(0, spacingLeftPercentage.length - 1) / 100
        ) * tableWidth
        tableRef.current.scrollTo(spacingLeft - tableRef.current.offsetWidth / 2, 0)
    }

    const zoomLevel = useSelector((state: RootState) => state.timetable.zoomLevel)
    useEffect(() => {
        if (tableRef.current && expanded) {
            scrollToEnter()
        }
    }, [zoomLevel, expanded])

    return (
        <S.Layout>
            <S.DateSelectWrapper>
                <DateControl />
            </S.DateSelectWrapper>

            <S.HeaderColumnWrapper>
                {programSpaces.map(({id, name}) => (
                    <S.AreaHeader key={id}>
                        {name}
                    </S.AreaHeader>
                ))}
            </S.HeaderColumnWrapper>

            <S.TimeTableWrapper ref={tableRef}>
                <S.TimeTableWidth>
                    <TimeGrid />
                    <div>
                        {programSpaces.map((area, i) => (
                            <AreaItemsRow key={area.id} index={i} area={area} />
                        ))}
                    </div>
                </S.TimeTableWidth>
            </S.TimeTableWrapper>

            <S.ZoomPosition>
                <ZoomControl />
            </S.ZoomPosition>
        </S.Layout>
    )
}

export default TimeTable
